
import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Phone } from 'lucide-react';

interface PhoneNumberFieldProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

const PhoneNumberField: React.FC<PhoneNumberFieldProps> = ({
  value,
  onChange,
  error,
  disabled = false
}) => {
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;
    
    // Remove all non-digit characters except +
    inputValue = inputValue.replace(/[^\d+]/g, '');
    
    // Handle different Ethiopian phone formats
    if (inputValue.startsWith('07')) {
      // Convert 07XXXXXXXX to +251XXXXXXXX
      inputValue = '+251' + inputValue.slice(1);
    } else if (inputValue.startsWith('+2517')) {
      // Handle +2517XXXXXXX (extra 7) - remove the extra 7
      inputValue = '+251' + inputValue.slice(5);
    }
    
    // Limit length to reasonable phone number size
    if (inputValue.length > 15) {
      inputValue = inputValue.slice(0, 15);
    }
    
    onChange(inputValue);
  };

  const formatPhoneDisplay = (phone: string) => {
    if (!phone) return '';
    
    // Ethiopian phone number formatting
    if (phone.startsWith('+251')) {
      const number = phone.slice(4);
      if (number.length >= 9) {
        return `+251 ${number.slice(0, 2)} ${number.slice(2, 5)} ${number.slice(5)}`;
      }
    }
    
    return phone;
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="phone_number" className="flex items-center gap-2">
        <Phone className="h-4 w-4" />
        Phone Number
      </Label>
      <div className="relative">
        <Input
          id="phone_number"
          type="tel"
          value={value}
          onChange={handlePhoneChange}
          placeholder="+251912345678 or 0912345678"
          disabled={disabled}
          className={error ? 'border-red-500' : ''}
        />
      </div>
      {value && (
        <div className="text-sm text-gray-600">
          Formatted: {formatPhoneDisplay(value)}
        </div>
      )}
      {error && (
        <div className="text-sm text-red-600">
          {error}
        </div>
      )}
      <div className="text-xs text-gray-500">
        Required for Telegram bot access. Use Ethiopian format: +251912345678 or 0912345678
      </div>
    </div>
  );
};

export default PhoneNumberField;
